import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  ListGroup,
  ListGroupItem,
  Row,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import WelcomeComp from "./WelcomeComp"
import { withTranslation } from "react-i18next"
import { FaCheck, FaMedal } from "react-icons/fa6"
import { DatePicker } from "antd"
import ReactApexChart from "react-apexcharts"
import PieChart from "./PieChart"
import FitnessStatsTable from "./fitneddstatstable"
import FitnessStatsChart from "./fitnessstatschart"
import BarChart from "./peakbarchart"
import { MdDownload } from "react-icons/md"
import { fetchData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import Configs from "../../config"
import user from "../../assets/images/user.png"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

const Dashboard = props => {
  const [selected, setSelected] = useState("")
  const [chartsData, setChartsData] = useState({})

  const [users, setusers] = useState([])
  const [selectedDate, setselectedDate] = useState([])
  const [loading, setloading] = useState(false)

  useEffect(() => {
    getUsers()
  }, [])

  async function getUsers() {
    const APIdata = await fetchData(setloading, "user/points/and/activities")

    if (APIdata.success) {
      setusers(APIdata?.users)
      setSelected(APIdata?.users?.[0]?._id)
    }
  }

  useEffect(() => {
    if (selected) {
      getUserData(selected)
    }
  }, [selected, selectedDate])

  async function getUserData(userId) {
    let url = `dashboard/${userId}`

    // Include dates only if selected
    if (selectedDate?.length === 2 && selectedDate[0] && selectedDate[1]) {
      url += `?startDate=${selectedDate[0]}&endDate=${selectedDate[1]}`
    }

    const APIdata = await fetchData(setloading, url)

    if (APIdata.success) {
      setChartsData(APIdata?.data)
    }
  }

  // Meta Title
  document.title = "Dashboard | Fans to 5K"

  const exportToExcel = (chartsData, fileName = "charts_data.xlsx") => {
    if (!chartsData || Object.keys(chartsData).length === 0) {
      alert("No data available to export.")
      return
    }

    const [fromDate, toDateRaw] = selectedDate || []
    const toDate = toDateRaw || new Date().toISOString().slice(0, 10)
    const selectedUser = users?.find(item => item?._id === selected)

    const workbook = XLSX.utils.book_new()
    const sheetData = []

    // Header section
    const headerSection = [
      ["User ID:", selected || "N/A"],
      ["Name:", selectedUser?.name || "N/A"],
      ["From:", fromDate || "N/A"],
      ["To:", toDate],
      [""],
    ]
    headerSection.forEach(row => sheetData.push(row))

    const customLabels = {
      planedDurationPieChart: "Fitness Summary: Planned Duration",
      planedDistancePieChart: "Fitness Summary: Planned Distance",
      completedDurationPieChart: "Fitness Summary: Completed Duration",
      completedDistancePieChart: "Fitness Summary: Completed Distance",
      graph: "Fitness History and Stats",
    }

    const pieChartKeys = [
      "planedDurationPieChart",
      "planedDistancePieChart",
      "completedDurationPieChart",
      "completedDistancePieChart",
    ]

    // Calculate max rows if needed
    let maxPieRows = 0
    pieChartKeys.forEach(key => {
      if (Array.isArray(chartsData[key])) {
        maxPieRows = Math.max(maxPieRows, chartsData[key].length + 1) // +1 for header row
      }
    })

    const spaceBetweenCharts = 1

    for (let rowIndex = 0; rowIndex < maxPieRows + 1; rowIndex++) {
      // +1 to include title row above label/header/data rows
      const row = []
      pieChartKeys.forEach((key, index) => {
        const data = chartsData[key]
        const hasData = Array.isArray(data) && data.length > 0
        const numCols = hasData ? Object.keys(data[0]).length : 0

        if (rowIndex === 0) {
          // Title row: Add chart label centered above its columns
          if (hasData) {
            row.push(customLabels[key])
            // Fill remaining columns in this block with empty cells for title row alignment
            for (let i = 1; i < numCols; i++) row.push("")
          } else {
            // No data - fill with blanks
            for (let i = 0; i < numCols; i++) row.push("")
          }
        } else if (rowIndex === 1) {
          // Header row for each chart columns
          if (hasData) {
            Object.keys(data[0]).forEach(header => row.push(header))
          } else {
            for (let i = 0; i < numCols; i++) row.push("")
          }
        } else {
          // Data rows: rowIndex - 2 because title and header rows occupy first two rows
          if (hasData) {
            const dataRow = data[rowIndex - 2]
            if (dataRow) {
              Object.values(dataRow).forEach(val => row.push(val))
            } else {
              for (let i = 0; i < numCols; i++) row.push("")
            }
          } else {
            for (let i = 0; i < numCols; i++) row.push("")
          }
        }

        // Add space columns between charts except after the last chart
        if (index < pieChartKeys.length - 1) {
          for (let s = 0; s < spaceBetweenCharts; s++) row.push("")
        }
      })
      sheetData.push(row)
    }

    // Empty row after pie charts
    sheetData.push([""])

    // Add graph table data below pie charts
    const graphData = chartsData.graph
    if (Array.isArray(graphData) && graphData.length > 0) {
      sheetData.push([customLabels.graph])
      sheetData.push(Object.keys(graphData[0]))
      graphData.forEach(row => {
        sheetData.push(Object.values(row))
      })
    }

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData)

    // Styling helpers
    const cellStyle = (cellRef, bold = false, fontSize = 12) => {
      if (!worksheet[cellRef]) return
      worksheet[cellRef].s = {
        font: { bold, sz: fontSize },
      }
    }

    // Style header section labels
    for (let r = 0; r < headerSection.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 0 })
      cellStyle(cellRef, true, 15)
    }

    // Style pie chart titles (first row of pie chart area)
    const pieChartTitleRow = headerSection.length
    const totalPieColumns =
      pieChartKeys.reduce((sum, key) => {
        const hasData =
          Array.isArray(chartsData[key]) && chartsData[key].length > 0
        return hasData
          ? sum + Object.keys(chartsData[key][0]).length + spaceBetweenCharts
          : sum + spaceBetweenCharts
      }, 0) - spaceBetweenCharts // Remove space after last chart

    for (let c = 0; c < totalPieColumns; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: pieChartTitleRow, c })
      cellStyle(cellRef, true, 13)
    }

    // Style pie chart headers (row below titles)
    const pieChartHeaderRow = pieChartTitleRow + 1
    for (let c = 0; c < totalPieColumns; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: pieChartHeaderRow, c })
      cellStyle(cellRef, true, 12)
    }

    // Style graph label row and headers if exist
    const graphLabelRow = sheetData.findIndex(
      row => row.length > 0 && row[0] === customLabels.graph
    )
    if (graphLabelRow !== -1) {
      cellStyle(XLSX.utils.encode_cell({ r: graphLabelRow, c: 0 }), true, 13)
      const graphHeaderRow = graphLabelRow + 1
      for (let c = 0; c < Object.keys(graphData[0]).length; c++) {
        cellStyle(XLSX.utils.encode_cell({ r: graphHeaderRow, c }), true, 12)
      }
    }

    // Set column widths with spacing considered
    const maxCols = Math.max(
      totalPieColumns,
      graphData ? Object.keys(graphData[0]).length : 0
    )
    worksheet["!cols"] = Array(maxCols).fill({ wch: 20 })

    XLSX.utils.book_append_sheet(workbook, worksheet, "Fitness Report")

    // Save file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    })
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" })
    saveAs(blob, fileName)
  }

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Dashboards")}
            breadcrumbItem={props.t("Dashboard")}
          />

          <Row className="px-0">
            {/* Left Panel - Athletes */}
            <Col xl="3" className="ps-md-0">
              {/* <WelcomeComp /> */}
              <Card className="Charts-card bg-white p-1">
                <CardHeader className="fw-bold bg-primary bg-soft text-black fs-5">
                  Athletes
                </CardHeader>
                <CardBody className="p-0">
                  <ListGroup flush>
                    {users.map((athlete, index) => {
                      const isSelected = selected === athlete._id

                      return (
                        <ListGroupItem
                          key={index}
                          onClick={() => setSelected(athlete._id)}
                          className={`d-flex align-items-center gap-3 ${
                            isSelected ? "bg-light border-primary" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            src={
                              athlete?.profilePicture
                                ? athlete?.profilePicture?.startsWith("https:")
                                  ? athlete?.profilePicture
                                  : `${Configs.imageurl}${athlete?.profilePicture}`
                                : user
                            }
                            alt={athlete.name}
                            className="rounded-circle"
                            height={50}
                            width={50}
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold fs-5">
                              {athlete.name}
                              {isSelected && (
                                <span className="badge bg-primary ms-2">
                                  Selected
                                </span>
                              )}
                            </div>
                            <div className="text-muted small fs-6">
                              <FaCheck className="me-1 text-success" />
                              {athlete.activities} activities
                              <span className="mx-2">|</span>
                              <FaMedal className="me-1 text-warning" />
                              {athlete.points} pts
                            </div>
                          </div>
                        </ListGroupItem>
                      )
                    })}
                    {users?.length === 0 && (
                      <div className="">No Users Found</div>
                    )}
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Right Panel - Chart */}
            <Col xl="9" className="pe-md-0">
              <Card className="p-3 Charts-card">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="text-black mb-0 bg-light border-primary p-2">
                    Performance Management - All Workout Types
                  </h6>
                  <div className="d-flex">
                    {/* <button
                      className="btn btn-outline-dark"
                      onClick={() =>
                        exportToExcel(
                          chartsData,
                          `${selected}_user_fitness_report.xlsx`
                        )
                      }
                    >
                      <MdDownload className="me-1" />
                      Export
                    </button> */}
                    <DatePicker.RangePicker
                      format="DD-MM-YYYY"
                      className="ms-3 custom-range-picker"
                      variant="filled"
                      onChange={dateStrings => {
                        console.log(dateStrings)
                        setselectedDate(dateStrings)
                      }}
                    />
                  </div>
                </div>
                <div className="row px-3">
                  {/* <div className="col-xl-8 ps-0">
                    <div className="border border-dark rounded-3 p-3 my-3">
                      <ReactApexChart
                        options={chartOptions}
                        series={chartSeries}
                        type="line"
                        height={255}
                      />
                    </div>
                  </div> */}

                  <div className="col-xl-4 pe-3 ps-0">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Fitness Summary: Planned Duration
                      </h6>
                      <PieChart
                        data={chartsData?.planedDurationPieChart}
                        type="duration"
                        subtype="planned"
                      />
                    </div>
                  </div>

                  <div className="col-xl-4 ps-0 pe-0">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Fitness Summary: Planned Distance
                      </h6>
                      <PieChart
                        data={chartsData?.planedDistancePieChart}
                        type="distance"
                        subtype="planned"
                      />
                    </div>
                  </div>

                  <div className="col-xl-4">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Fitness Summary: Completed Duration
                      </h6>
                      <PieChart
                        data={chartsData?.completedDurationPieChart}
                        type="duration"
                        subtype="completed"
                      />
                    </div>
                  </div>

                  <div className="col-xl-4 pe-3 ps-0">
                    <div
                      className=" border border-dark rounded-3 p-3 my-3"
                      style={{ height: "345px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Fitness Summary: Completed Distance
                      </h6>
                      <PieChart
                        data={chartsData?.completedDistancePieChart}
                        type="distance"
                        subtype="completed"
                      />
                    </div>
                  </div>

                  <div className="col-xl-8 ps-0 pe-0">
                    <div className=" border border-dark rounded-3 p-3 my-3">
                      <h6 className="mb-3 text-center">
                        Fitness History: Peak Heart Rate (bpm) - All Workout
                        Types
                      </h6>
                      <FitnessStatsTable data={chartsData?.graph} />
                    </div>
                  </div>

                  <div className="col-xl-6 ps-0 pe-0">
                    <div className="border border-dark rounded-3 p-3 my-3">
                      <h6 className="mb-3 text-center">
                        Longest Workout (Duration): All Workout Types
                      </h6>
                      <FitnessStatsChart
                        data={chartsData?.graph}
                        type="duration"
                      />
                    </div>
                  </div>

                  <div className="col-xl-6 pe-0">
                    <div className="border border-dark rounded-3 p-3 my-3">
                      <h6 className="mb-3 text-center">
                        Longest Workout (Distance): All Workout Types
                      </h6>
                      <FitnessStatsChart
                        data={chartsData?.graph}
                        type="distance"
                      />
                    </div>
                  </div>

                  <div className="col-xl-12 px-0 ">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">Calories</h6>
                      <BarChart data={chartsData?.graph} type="calories" />
                    </div>
                  </div>

                  {/* <div className="col-xl-12 ps-0 pe-0">
                    <div className=" border border-dark rounded-3 p-3 my-3">
                      <h6 className="mb-3 text-center">
                        Fitness History: Peak Power (W) - All Workout Types
                      </h6>
                      <FitnessStatsTable />
                    </div>
                  </div>

                  <div className="col-xl-4 ps-0">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Peaks - Heart Rate: All Workout Types
                      </h6>
                      <BarChart />
                    </div>
                  </div>

                  <div className="col-xl-4 ps-0 pe-0">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Peaks - Power : All Workout Types
                      </h6>
                      <BarChart />
                    </div>
                  </div>

                  <div className="col-xl-4 pe-0 ">
                    <div
                      className="border border-dark rounded-3 p-3 my-3"
                      style={{ height: "300px" }}
                    >
                      <h6 className="mb-3 text-center">
                        Peak Pace by Distance : Run
                      </h6>
                      <BarChart />
                    </div>
                  </div> */}

                  {/* <div className="col-xl-4 px-0">
                    <div className="border border-dark rounded-3 p-3 my-3">
                      <h6 className="mb-3 text-center">
                        Power Profile (Cycling): Single Group
                      </h6>
                      <ReactApexChart
                        options={chartOptions}
                        series={chartSeries}
                        type="line"
                        height={220}
                      />
                    </div>
                  </div> */}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(Dashboard)
